import React from "react";
import { useTranslation } from "react-i18next";
import Form, { FORM_HORIZONTAL_LAYOUT } from "../../../../components/Form";
import { useDateFormat } from "../../../../utils/hooks/useDateFormat";
import { REPORT_FORM_TYPE } from "../index";
import { useLoggedUser } from "../../../../utils/hooks/useLoggedUser";
import Modal from "../../../../components/Modal";
import { AddCorrectiveActionForm } from "../../CorrectiveActions/AddCorrectiveActionForm";
import { MODAL_IOR_CORRECTIVE_ACTION } from "../../CorrectiveActions";
import { ReportBasicData } from "./ReportBasicData";
import { getFormInitialValues } from "./methods";
import { ReportFirstRiskAnalysis } from "./ReportFirstRiskAnalysis";
import { ReportInvestigation } from "./ReportInvestigation";
import { ReportRootCause } from "./ReportRootCause";
import { ReportRiskMatrix } from "./ReportRiskMatrix";
import { ReportManagementSummary } from "./ReportManagementSummary";
import { ReportSecondRiskAnalysis } from "./ReportSecondRiskAnalysis";
import { DocumentForm } from "./ReportBasicData/DocumentForm";
import { MODAL_IOR_DOCUMENT_FORM } from "./ReportBasicData/DocumentsTable";
import { ReportButtons } from "./ReportButtons";
import ReportInvestigators from "./ReportInvestigators";
import { ReportCorrectiveActions } from "./ReportCorrectiveActions";
import { ReportInternalNotes } from "./ReportInternalNotes";
import useReportFormState from "./useReportFormState";

/**
 * Enum-like object identifying which page the user navigated to this report from.
 * Stored in router location.state so the back-navigation can return to the correct list.
 */
export const IOR_REPORT_PAGE = {
  REPORTS: "REPORTS",
  MY_REPORTS: "MY_REPORTS"
};

/**
 * Report is the top-level form component for creating and editing IOR (Incident/Occurrence Report) records.
 *
 * The form is divided into independently saveable sections rendered as collapsible panels:
 * BasicData, Investigation, RootCause, Investigators, FirstRiskAnalysis, CorrectiveActions,
 * SecondRiskAnalysis (conditional), ManagementSummary, RiskMatrix, and InternalNotes.
 *
 * @param {object}  props
 * @param {object}  [props.generalSettings={}]      - Global IOR settings (e.g. can_be_reopened flag)
 * @param {object}  [props.data={}]                 - Existing report data for pre-populating the form
 * @param {string}  props.type                      - Form mode: "new" | "edit" | "view"
 * @param {boolean} props.editOnlyBasicSection       - When true, only the basic data section is editable
 * @returns {React.ReactElement} The full IOR report form wrapped in an Ant Design Form
 */
export const Report = ({ generalSettings = {}, data = {}, type, editOnlyBasicSection }) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const loggedUser = useLoggedUser();
  const { formatLocaleUTC, format } = useDateFormat();

  // Watch the risk level field to know whether a second risk analysis section is needed
  const formWatch = Form.useWatch(["riskAnalysis", "riskLevel"], form);

  const {
    success,
    setSuccess,
    collapseErrors,
    setCollapseErrors,
    hasSecondRiskAnalysis,
    setIsRootCause,
    isRootCauseCollapsed,
    basicDataSaved,
    setBasicDataSaved,
    hasRootCauseError,
    loading,
    generalError,
    riskMatrix,
    formatLocale,
    onClose,
    updateRootCauses,
    submitForm
  } = useReportFormState({ data, form, formWatch, formatLocaleUTC, format });

  return (
    <div className="box">
      <Modal name={MODAL_IOR_DOCUMENT_FORM}>
        <DocumentForm />
      </Modal>
      <Modal name={MODAL_IOR_CORRECTIVE_ACTION}>
        <AddCorrectiveActionForm name={MODAL_IOR_CORRECTIVE_ACTION} reportForm={form} basicDataSaved={basicDataSaved} />
      </Modal>
      {!success && (
        <Form
          onSubmit={submitForm}
          form={form}
          errors={generalError}
          loading={loading}
          layout={FORM_HORIZONTAL_LAYOUT}
          initialValues={getFormInitialValues(data, formatLocale, loggedUser, type, t)}
        >
          <div className="flex flex-justify-between flex-items-center mb-30">
            <div className="h4 mb-10">
              {(() => {
                const typeSuffix = type === REPORT_FORM_TYPE.NEW ? "" : `_${type}`;
                return t(`ior.new_report.page_title${typeSuffix}`);
              })()}
            </div>
            {!editOnlyBasicSection && (
              <ReportButtons
                submitLoading={loading}
                onClose={onClose}
                submit={submitForm}
                type={type}
                data={data}
                setSuccess={setSuccess}
                form={form}
                collapseErrors={collapseErrors}
                setCollapseErrors={setCollapseErrors}
                canBeReopened={generalSettings?.can_be_reopened}
                basicDataSaved={basicDataSaved}
              />
            )}
          </div>
          <ReportBasicData
            form={form}
            data={data}
            type={type}
            hasError={collapseErrors.basic_data}
            setBasicDataSaved={setBasicDataSaved}
            basicDataSaved={basicDataSaved}
            editOnlyBasicSection={editOnlyBasicSection}
          />
          {!editOnlyBasicSection && (
            <>
              <ReportInvestigation
                form={form}
                data={data}
                type={type}
                hasError={collapseErrors.investigation}
                initialValues={data?.investigation}
                basicDataSaved={basicDataSaved}
              />

              {/* Hide RootCause for archived/closed reports still using the old single root-cause format */}
              {!data?.basic_data ||
              (data.basic_data.stage !== "archived" &&
                !(data.basic_data.stage === "closed" && data.basic_data.root_causes_old_format === 1)) ? (
                <ReportRootCause
                  form={form}
                  data={data}
                  type={type}
                  loading={loading}
                  setIsRootCause={setIsRootCause}
                  isCollapsed={isRootCauseCollapsed}
                  hasError={hasRootCauseError}
                  onUpdateRootCauses={updateRootCauses}
                  basicDataSaved={basicDataSaved}
                  isClosedReport={data.basic_data?.stage === "closed"}
                />
              ) : (
                <></>
              )}
              <ReportInvestigators type={type} basicDataSaved={basicDataSaved} />
              <ReportFirstRiskAnalysis
                form={form}
                data={data}
                hasSecondRiskAnalysis={hasSecondRiskAnalysis}
                type={type}
                hasError={collapseErrors.first_risk_analysis}
                basicDataSaved={basicDataSaved}
              />
              <ReportCorrectiveActions form={form} data={data} type={type} basicDataSaved={basicDataSaved} />
              {hasSecondRiskAnalysis && (
                <ReportSecondRiskAnalysis
                  form={form}
                  data={data}
                  type={type}
                  hasError={collapseErrors.second_risk_analysis}
                  basicDataSaved={basicDataSaved}
                />
              )}
              <ReportManagementSummary
                form={form}
                data={data}
                type={type}
                hasError={collapseErrors.management_summary}
                basicDataSaved={basicDataSaved}
              />
              <ReportRiskMatrix
                data={riskMatrix.data?.data?.data}
                loading={riskMatrix.loading}
                isSectionDisabled={!basicDataSaved}
              />
              <ReportInternalNotes data={data} basicDataSaved={basicDataSaved} />
            </>
          )}
          {!editOnlyBasicSection && (
            <div className="flex flex-justify-end flex-items-center mt-20 mb-10">
              <ReportButtons
                submitLoading={loading}
                onClose={onClose}
                submit={submitForm}
                type={type}
                data={data}
                setSuccess={setSuccess}
                form={form}
                collapseErrors={collapseErrors}
                setCollapseErrors={setCollapseErrors}
                canBeReopened={generalSettings?.can_be_reopened}
                basicDataSaved={basicDataSaved}
              />
            </div>
          )}
        </Form>
      )}
    </div>
  );
};
